<template>
    <div :data-upload-state="uploadState" class="space-y-2">
        <label class="block text-sm font-medium text-gray-700">
            {{ $t("checkout.identity.uploadTitle") }} <span v-if="required" class="text-red-500">*</span>
        </label>

        <div
            class="rounded-lg border border-gray-300 bg-white p-3"
            :class="[
                uploadState === 'error' ? 'border-red-300 bg-red-50' : '',
                uploadState === 'success'
                    ? 'border-green-300 bg-green-50'
                    : 'border-gray-300 bg-white',
            ]"
        >
            <div class="flex flex-wrap items-center justify-between gap-2">
                <div class="min-w-0">
                    <p class="truncate text-sm text-gray-700" :title="fullFileName">{{ fileLabel }}</p>
                    <p class="text-xs text-gray-500">
                        {{ $t("checkout.identity.hint") }}
                    </p>
                </div>

                <label
                    class="inline-flex cursor-pointer items-center rounded-lg border border-blue-500 px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50"
                >
                    {{ $t("checkout.identity.chooseFile") }}
                    <input
                        data-testid="identity-file"
                        ref="fileInput"
                        type="file"
                        class="hidden"
                        accept="image/*"
                        @change="onFileChange"
                    />
                </label>
            </div>

            <div v-if="uploadState === 'uploading'" class="mt-3">
                <div class="h-2 w-full rounded bg-blue-100">
                    <div
                        class="h-2 rounded bg-blue-600"
                        :style="{ width: `${uploadProgress}%` }"
                    />
                </div>
                <p class="mt-1 text-xs text-blue-700">
                    {{ $t("checkout.identity.uploading", { percent: uploadProgress }) }}
                </p>
            </div>

            <div
                v-if="uploadState === 'success'"
                class="mt-3 rounded-lg border border-green-200 bg-white p-2"
            >
                <div class="flex items-center justify-between gap-3">
                    <div class="flex min-w-0 flex-1 items-center gap-2">
                        <img
                            v-if="modelValue.isImage && modelValue?.previewUrl"
                            :src="modelValue.previewUrl"
                            :alt="$t('checkout.identity.previewAlt')"
                            class="h-14 w-14 rounded-md border object-cover"
                        />
                        <div
                            v-else
                            class="flex h-14 w-14 items-center justify-center rounded-md border bg-white"
                        >
                            <AppIcon
                                icon="mdi:card-account-details-outline"
                                class="h-7 w-7 text-blue-600"
                            />
                        </div>
                        <div class="min-w-0">
                            <p class="truncate text-xs font-medium text-green-700">
                                {{ $t("checkout.identity.uploaded") }}
                            </p>
                            <p class="truncate text-xs text-gray-500" :title="fullFileName">
                                {{ uploadedLabel }}
                            </p>
                        </div>
                    </div>

                    <div class="flex items-center gap-2">
                        <button
                            v-if="modelValue?.previewUrl"
                            type="button"
                            :title="$t('checkout.identity.view')"
                            class="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                            @click="previewFile"
                        >
                            <AppIcon icon="mdi:eye-outline" class="h-4 w-4" />
                        </button>
                        <button
                            type="button"
                            data-testid="identity-remove"
                            :title="$t('checkout.identity.delete')"
                            class="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-red-300 text-red-600 hover:bg-red-50"
                            @click="removeFile"
                        >
                            <AppIcon
                                icon="mdi:trash-can-outline"
                                class="h-4 w-4"
                            />
                        </button>
                    </div>
                </div>
            </div>

            <p v-if="uploadState === 'error'" data-testid="identity-upload-error" class="mt-2 text-xs text-red-600">
                {{ errorMessage || $t("checkout.identity.uploadFailed") }}
            </p>

            <button
                v-if="uploadState === 'error' && selectedFile"
                type="button"
                class="mt-2 inline-flex items-center rounded-lg border border-red-400 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
                data-testid="identity-upload-retry"
                @click="retryUpload"
            >
                {{ $t("checkout.identity.retry") }}
            </button>
        </div>
    </div>
</template>

<script>
import { API_URL } from "@/mainconfig";
import { truncateFileName } from "@/utils/fileName.mjs";

export default {
    name: "GuestIdentityUpload",
    props: {
        required: {
            type: Boolean,
            default: true,
        },
        modelValue: {
            type: Object,
            default: () => ({
                imageId: null,
                fileName: "",
                previewUrl: "",
                originalUrl: "",
                isImage: false,
                uploadState: "idle",
                errorMessage: "",
            }),
        },
    },
    data() {
        return {
            uploadProgress: 0,
            timer: null,
            selectedFile: null,
        };
    },
    computed: {
        uploadState() {
            return this.modelValue?.uploadState || "idle";
        },
        errorMessage() {
            return this.modelValue?.errorMessage || "";
        },
        fileLabel() {
            if (this.modelValue?.fileName) return truncateFileName(this.modelValue.fileName);
            return this.$t("checkout.identity.noFile");
        },
        uploadedLabel() {
            if (this.modelValue?.fileName) return truncateFileName(this.modelValue.fileName);
            return this.$t("checkout.identity.defaultName");
        },
        // Nama utuh tetap bisa dilihat lewat hover/tekan-lama, tanpa memakan lebar kartu.
        fullFileName() {
            return this.modelValue?.fileName || "";
        },
    },
    beforeUnmount() {
        if (this.timer) clearInterval(this.timer);
    },
    methods: {
        onFileChange(event) {
            const file = event.target.files?.[0];
            if (!file) return;
            this.selectedFile = file;
            this.startUpload(file);
        },
        retryUpload() {
            if (!this.selectedFile) return;
            this.startUpload(this.selectedFile);
        },
        previewFile() {
            const openUrl =
                this.modelValue?.originalUrl || this.modelValue?.previewUrl;
            if (!openUrl) return;
            window.open(openUrl, "_blank", "noopener,noreferrer");
        },
        buildImageUrl(imageId, original = false) {
            if (!imageId) return "";
            const baseUrl = String(API_URL?.defaults?.baseURL || "").replace(
                /\/$/,
                ""
            );
            if (!baseUrl) return "";
            return `${baseUrl}/image/${imageId}${
                original ? "?original=true" : ""
            }`;
        },
        extractImageId(payload) {
            if (payload == null) return null;
            if (typeof payload === "number" || typeof payload === "string") {
                const numericId = Number(payload);
                return Number.isFinite(numericId) && numericId > 0
                    ? numericId
                    : null;
            }
            if (Array.isArray(payload)) {
                for (const item of payload) {
                    const imageId = this.extractImageId(item);
                    if (imageId) return imageId;
                }
                return null;
            }
            if (typeof payload === "object") {
                const directId = Number(
                    payload.Id ??
                        payload.id ??
                        payload.ImageId ??
                        payload.imageId ??
                        payload.IdentityImageId ??
                        null
                );
                if (Number.isFinite(directId) && directId > 0) {
                    return directId;
                }
                return (
                    this.extractImageId(payload.data) ||
                    this.extractImageId(payload.rows) ||
                    this.extractImageId(payload.result) ||
                    this.extractImageId(payload.images) ||
                    this.extractImageId(payload.items) ||
                    null
                );
            }
            return null;
        },
        removeFile() {
            this.selectedFile = null;
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = "";
            }

            this.$emit("update:modelValue", {
                ...this.modelValue,
                imageId: null,
                fileName: "",
                previewUrl: "",
                originalUrl: "",
                isImage: false,
                uploadState: "idle",
                errorMessage: "",
            });
        },
        readFileAsDataUrl(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result || "");
                reader.onerror = () =>
                    reject(new Error(this.$t("checkout.identity.readFailed")));
                reader.readAsDataURL(file);
            });
        },
        async startUpload(file) {
            const isImage = file.type.startsWith("image/");
            const allowedType = isImage;
            const maxSize = 5 * 1024 * 1024;
            const previewUrl = isImage ? await this.readFileAsDataUrl(file) : "";

            if (!allowedType) {
                this.$emit("update:modelValue", {
                    ...this.modelValue,
                    fileName: file.name,
                    previewUrl,
                    originalUrl: "",
                    isImage,
                    uploadState: "error",
                    errorMessage: this.$t("checkout.identity.unsupportedFormat"),
                });
                return;
            }

            if (file.size > maxSize) {
                this.$emit("update:modelValue", {
                    ...this.modelValue,
                    fileName: file.name,
                    previewUrl,
                    originalUrl: "",
                    isImage,
                    uploadState: "error",
                    errorMessage: this.$t("checkout.identity.tooLarge"),
                });
                return;
            }

            this.$emit("update:modelValue", {
                ...this.modelValue,
                fileName: file.name,
                previewUrl,
                originalUrl: "",
                isImage,
                uploadState: "uploading",
                errorMessage: "",
            });

            this.uploadProgress = 0;
            try {
                const formData = new FormData();
                formData.append("images", file);

                const response = await API_URL.post("/image", formData, {
                    requiresAuth: true,
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                    onUploadProgress: (progressEvent) => {
                        const loaded = progressEvent.loaded || 0;
                        const total = progressEvent.total || 0;
                        this.uploadProgress = total
                            ? Math.min(
                                  99,
                                  Math.round((loaded / total) * 100)
                              )
                            : 50;
                    },
                });

                const imageId = this.extractImageId(response?.data ?? response);

                if (!Number.isFinite(imageId) || imageId <= 0) {
                    throw new Error(this.$t("checkout.identity.imageIdMissing"));
                }

                this.uploadProgress = 100;
                const thumbUrl = this.buildImageUrl(imageId, false);
                const originalUrl = this.buildImageUrl(imageId, true);
                this.$emit("update:modelValue", {
                    ...this.modelValue,
                    fileName: file.name,
                    previewUrl: thumbUrl || previewUrl,
                    originalUrl: originalUrl || thumbUrl || previewUrl,
                    isImage,
                    imageId,
                    uploadState: "success",
                    errorMessage: "",
                });
                this.$emit("uploaded", {
                    fileName: file.name,
                    imageId,
                });
            } catch (error) {
                const serverMessage =
                    error?.response?.data?.msg ||
                    error?.message ||
                    this.$t("checkout.identity.uploadFailed");
                this.$emit("update:modelValue", {
                    ...this.modelValue,
                    fileName: file.name,
                    previewUrl,
                    originalUrl: "",
                    isImage,
                    imageId: null,
                    uploadState: "error",
                    errorMessage: serverMessage,
                });
            }
        },
    },
};
</script>
